import { ApiResponse } from './types';
import { apiClient } from './core/apiClient';
import {
  IAddScheduleExceptionRequest,
  ICreateScheduleRequest,
  IHoliday,
  IProfessionalSchedule,
  IScheduleException,
  IUpdateScheduleRequest,
} from '../types/interfaces/ISchedule';

interface ScheduleExceptionApiDto {
  id: string;
  professionalScheduleId: string;
  date: string;
  exceptionType: IScheduleException['exceptionType'];
  startTime?: string | null;
  endTime?: string | null;
  durationMinutes?: number | null;
  reason?: string | null;
  createdByUserId: string;
  createdAt: string;
}

interface ProfessionalScheduleApiDto {
  id: string;
  userId: string;
  institutionId: string;
  specialtyId: string;
  dayOfWeek: IProfessionalSchedule['dayOfWeek'];
  startTime: string;
  endTime: string;
  appointmentDurationMinutes: number;
  breakBetweenMinutes: number;
  maxPatientsPerDay?: number | null;
  validFrom: string;
  validTo?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  exceptions?: ScheduleExceptionApiDto[];
}

interface HolidayApiDto {
  id: string;
  date: string;
  name: string;
  type: IHoliday['type'];
  institutionId?: string | null;
  source: IHoliday['source'];
}

function normalizeScheduleException(
  exceptionItem: ScheduleExceptionApiDto
): IScheduleException {
  return {
    id: exceptionItem.id,
    professionalScheduleId: exceptionItem.professionalScheduleId,
    date: exceptionItem.date,
    exceptionType: exceptionItem.exceptionType,
    startTime: exceptionItem.startTime || undefined,
    endTime: exceptionItem.endTime || undefined,
    durationMinutes: exceptionItem.durationMinutes || undefined,
    reason: exceptionItem.reason || undefined,
    createdByUserId: exceptionItem.createdByUserId,
    createdAt: exceptionItem.createdAt,
  };
}

function normalizeSchedule(
  schedule: ProfessionalScheduleApiDto
): IProfessionalSchedule {
  return {
    id: schedule.id,
    userId: schedule.userId,
    institutionId: schedule.institutionId,
    specialtyId: schedule.specialtyId,
    dayOfWeek: schedule.dayOfWeek,
    startTime: schedule.startTime,
    endTime: schedule.endTime,
    appointmentDurationMinutes: schedule.appointmentDurationMinutes,
    breakBetweenMinutes: schedule.breakBetweenMinutes,
    maxPatientsPerDay: schedule.maxPatientsPerDay || undefined,
    validFrom: schedule.validFrom,
    validTo: schedule.validTo || undefined,
    isActive: schedule.isActive,
    createdAt: schedule.createdAt,
    updatedAt: schedule.updatedAt,
    exceptions: (schedule.exceptions || []).map(normalizeScheduleException),
  };
}

function normalizeHoliday(holiday: HolidayApiDto): IHoliday {
  return {
    id: holiday.id,
    date: holiday.date,
    name: holiday.name,
    type: holiday.type,
    institutionId: holiday.institutionId || undefined,
    source: holiday.source,
  };
}

export async function getSchedules(
  institutionId: string
): Promise<ApiResponse<IProfessionalSchedule[]>> {
  const response = await apiClient.get<ProfessionalScheduleApiDto[]>('/schedules', {
    institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  return {
    ...response,
    data: response.data.map(normalizeSchedule),
  };
}

export async function createSchedule(
  payload: ICreateScheduleRequest
): Promise<ApiResponse<IProfessionalSchedule>> {
  const response = await apiClient.post<ProfessionalScheduleApiDto>('/schedules', payload);

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IProfessionalSchedule,
    };
  }

  return {
    ...response,
    data: normalizeSchedule(response.data),
  };
}

export async function updateSchedule(
  id: string,
  payload: IUpdateScheduleRequest
): Promise<ApiResponse<IProfessionalSchedule>> {
  const response = await apiClient.put<ProfessionalScheduleApiDto>(
    `/schedules/${id}`,
    payload
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IProfessionalSchedule,
    };
  }

  return {
    ...response,
    data: normalizeSchedule(response.data),
  };
}

export async function addScheduleException(
  scheduleId: string,
  payload: IAddScheduleExceptionRequest
): Promise<ApiResponse<IScheduleException>> {
  const response = await apiClient.post<ScheduleExceptionApiDto>(
    `/schedules/${scheduleId}/exceptions`,
    payload
  );

  if (!response.success || !response.data) {
    return {
      ...response,
      data: null as unknown as IScheduleException,
    };
  }

  return {
    ...response,
    data: normalizeScheduleException(response.data),
  };
}

export async function getHolidays(
  year: number,
  institutionId?: string
): Promise<ApiResponse<IHoliday[]>> {
  const response = await apiClient.get<HolidayApiDto[]>('/holidays', {
    year,
    institutionId,
  });

  if (!response.success || !response.data) {
    return {
      ...response,
      data: [],
    };
  }

  return {
    ...response,
    data: response.data.map(normalizeHoliday),
  };
}